import React from 'react';

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="font-semibold text-xtnd-blue text-xl">XTND</span>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">•</span>
            <span className="font-medium text-xtnd-dark dark:text-xtnd-white text-xl">Privacy Policy</span>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-xtnd-dark dark:prose-headings:text-xtnd-white prose-p:text-xtnd-dark-600 dark:prose-p:text-xtnd-dark-300 prose-a:text-xtnd-blue hover:prose-a:text-xtnd-blue-600">

          <div className="mb-6">
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 mb-2">
              <strong>Effective Date:</strong> June 1, 2025
            </p>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400">
              <strong>Last Revised:</strong> August 1, 2025
            </p>
          </div>

          <p className="text-xtnd-dark-600 dark:text-xtnd-dark-300 mb-8">
            This Privacy Policy describes how The Coral Edge, Inc. ("The Coral Edge, Inc.", "we", "our", or "us") collects, uses, and discloses personal data through our website, platform, APIs, and services (collectively, the "XTND Service"). By using the XTND Service, you consent to the collection and use of your information as described in this policy.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">1. Sites and Operator</h2>
          <ul className="mb-8">
            <li><strong>Website</strong>: <a href="https://xtnd.legal" target="_blank" rel="noopener noreferrer">https://xtnd.legal</a></li>
            <li><strong>Mailing Address</strong>: PO Box 10071, Wilmington, NC 28404</li>
            <li><strong>Contact</strong>: <a href="mailto:support@xtnd.legal">support@xtnd.legal</a></li>
          </ul>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">2. Jurisdiction-Specific Privacy Rights</h2>
          <p className="mb-4">
            XTND complies with applicable data privacy laws, including but not limited to:
          </p>
          <ul className="mb-4">
            <li>California Consumer Privacy Act (CCPA/CPRA)</li>
            <li>Colorado Privacy Act (CPA)</li>
            <li>Virginia Consumer Data Protection Act (VCDPA)</li>
            <li>General Data Protection Regulation (GDPR)</li>
            <li>UK GDPR</li>
            <li>Canada's PIPEDA</li>
            <li>Australia's Privacy Act 1988</li>
            <li>New Zealand's Privacy Act 2020</li>
          </ul>
          <p className="mb-4">
            For clients in these jurisdictions, we ensure data transfers meet adequacy standards (e.g., via SCCs for GDPR/UK GDPR if any non-U.S. transfers occur, though data is hosted exclusively in U.S. AWS regions).
          </p>
          <p className="mb-8">
            You may request access, correction, deletion, restriction, or portability of your data, or opt out of sales/sharing by emailing: <a href="mailto:support@xtnd.legal">support@xtnd.legal</a>. For EU/UK/Canadian residents, we recommend executing a Data Processing Addendum (DPA).
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">3. Information We Collect</h2>
          <p className="mb-4">XTND collects personal and non-personal data:</p>

          <h3 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white mb-3">User-Provided Data</h3>
          <ul className="mb-4">
            <li>Full name, firm name, email, phone</li>
            <li>Clio Client ID, Clio Client Secret, Clio Authorization Code</li>
            <li>Payment/billing details (processed via Stripe)</li>
            <li>Case/matter metadata (from Clio or QBO)</li>
            <li>Financial and transaction data (e.g., trust account details, invoice metadata, matter ledgers)</li>
            <li>JSON transaction exports</li>
          </ul>

          <h3 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white mb-3">Automatically Collected Data</h3>
          <ul className="mb-4">
            <li>Device/browser details</li>
            <li>IP address</li>
            <li>Usage logs and event data</li>
          </ul>

          <h3 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white mb-3">Third-Party Integrations</h3>
          <ul className="mb-4">
            <li>Clio, QuickBooks Online, Google APIs (via SSO)</li>
            <li>Azure (for secure storage of transaction exports)</li>
            <li>LocalStack (development environments only)</li>
          </ul>

          <div className="bg-xtnd-light dark:bg-xtnd-dark-800 p-4 rounded-lg mb-8">
            <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
              <strong>Note:</strong> XTND does not collect or store user login credentials. Authentication is handled via secure single sign-on (SSO) with Google, Microsoft, or Clio.
            </p>
          </div>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">4. How We Use Information</h2>
          <p className="mb-4">We use your data to:</p>
          <ul className="mb-8">
            <li>Authenticate and manage user access via SSO</li>
            <li>Process payments and generate billing documents</li>
            <li>Facilitate API integrations (Clio, QBO)</li>
            <li>Provide customer support and service improvements</li>
            <li>Ensure platform security and compliance</li>
            <li>Automate billing workflows</li>
            <li>Send notifications and system alerts</li>
          </ul>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">5. Legal Bases for Processing (GDPR)</h2>
          <ul className="mb-8">
            <li><strong>Contractual necessity</strong>: Provide the XTND Service</li>
            <li><strong>Legitimate interest</strong>: Platform analytics, fraud prevention</li>
            <li><strong>Consent</strong>: Marketing communications (opt-in)</li>
            <li><strong>Legal obligation</strong>: Regulatory compliance</li>
          </ul>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">6. Data Processor Role</h2>
          <p className="mb-8">
            XTND acts as a data processor for any data you upload or process via the XTND Service (e.g., client data, invoices, trust transactions). We process data only under your instructions and in line with applicable laws. DPAs are available upon request.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">7. Data Sharing and Disclosure</h2>
          <p className="mb-4">XTND shares data with:</p>
          <ul className="mb-4">
            <li>Cloud/infrastructure providers (e.g. AWS)</li>
            <li>Integration partners (Clio, QBO)</li>
            <li>Subprocessors (see section below)</li>
            <li>Law enforcement/regulators when required</li>
            <li>Storage providers (e.g., Azure Blob for exports)</li>
          </ul>

          <div className="bg-xtnd-light dark:bg-xtnd-dark-800 p-4 rounded-lg mb-8">
            <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
              <strong>XTND does not sell your personal data.</strong>
            </p>
          </div>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">8. Cookies and Analytics</h2>
          <p className="mb-8">
            XTND uses cookies and analytics tools (e.g., Google Analytics, AWS CloudWatch) to enhance performance, security, and usability. Users may control cookie settings in their browsers or use opt-out tools.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">9. Data Security</h2>
          <ul className="mb-8">
            <li>TLS/SSL encryption (in transit)</li>
            <li>Encryption at rest via AWS KMS</li>
            <li>Access controls and permissions</li>
            <li>Secret management via AWS</li>
            <li>Vulnerability scanning and incident response</li>
          </ul>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">10. Data Residency and Transfers</h2>
          <p className="mb-8">
            All data is hosted exclusively in AWS U.S. regions. If international transfers are needed (e.g., for Clio/QBO integration), we apply encryption and contractual protections. Additional consent may be required under certain laws.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">11. Your Rights and Choices</h2>
          <p className="mb-4">You have the right to:</p>
          <ul className="mb-4">
            <li>Access, correct, delete, or restrict personal data</li>
            <li>Object to direct marketing</li>
            <li>Request machine-readable data portability</li>
          </ul>

          <p className="mb-8">
            We retain data as needed for service delivery and legal compliance (e.g., 7 years for IRS-related records). Email: <a href="mailto:support@xtnd.legal">support@xtnd.legal</a> to submit requests.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">12. Breach Notification</h2>
          <p className="mb-8">
            If a breach occurs affecting your personal data, XTND will notify affected users without unreasonable delay and within timeframes required by law (e.g., 72 hours under GDPR/UK GDPR, or 30–60 days under U.S. laws).
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">13. Children's Privacy</h2>
          <p className="mb-8">
            XTND does not knowingly collect data from individuals under 18. Contact us immediately if you believe otherwise.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">14. Do Not Track</h2>
          <p className="mb-8">
            XTND does not respond to browser-based "Do Not Track" signals. Third-party providers may not honor these either.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">15. Updates to This Policy</h2>
          <p className="mb-8">
            We will notify users of material changes via email or dashboard message. Continued use after notice constitutes acceptance.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">16. Complaints and Dispute Resolution</h2>
          <p className="mb-8">
            You may contact: <a href="mailto:support@xtnd.legal">support@xtnd.legal</a><br />
            You also have the right to contact your local data protection authority.
          </p>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">17. Data Categories Table</h2>
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full border border-xtnd-light dark:border-xtnd-dark-600">
              <thead className="bg-xtnd-light dark:bg-xtnd-dark-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xtnd-dark dark:text-xtnd-white font-semibold border-b border-xtnd-light dark:border-xtnd-dark-600">Category</th>
                  <th className="px-4 py-2 text-left text-xtnd-dark dark:text-xtnd-white font-semibold border-b border-xtnd-light dark:border-xtnd-dark-600">Examples</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Identity Data</td>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Name, email, firm affiliation</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Technical Data</td>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">IP address, browser type, usage logs</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Financial Data</td>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Invoicing, payment info</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Integration Data</td>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Clio Client ID, Secret, Authorization Code, Matter metadata</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-xtnd-dark-600 dark:text-xtnd-dark-300">Transaction Data</td>
                  <td className="px-4 py-2 text-xtnd-dark-600 dark:text-xtnd-dark-300">Trust balances, invoice details, ledgers, JSON exports</td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">18. Legal Basis Table</h2>
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full border border-xtnd-light dark:border-xtnd-dark-600">
              <thead className="bg-xtnd-light dark:bg-xtnd-dark-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xtnd-dark dark:text-xtnd-white font-semibold border-b border-xtnd-light dark:border-xtnd-dark-600">Basis</th>
                  <th className="px-4 py-2 text-left text-xtnd-dark dark:text-xtnd-white font-semibold border-b border-xtnd-light dark:border-xtnd-dark-600">Applicable Use</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Contract</td>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Platform access, support</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Legitimate Interest</td>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Performance monitoring</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Consent</td>
                  <td className="px-4 py-2 border-b border-xtnd-light dark:border-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-300">Marketing communications</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-xtnd-dark-600 dark:text-xtnd-dark-300">Legal Obligation</td>
                  <td className="px-4 py-2 text-xtnd-dark-600 dark:text-xtnd-dark-300">Compliance with laws</td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr className="my-8 border-xtnd-light dark:border-xtnd-dark-600" />

          <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-4">19. Subprocessors</h2>
          <p className="mb-8">
            XTND uses trusted subprocessors (e.g., AWS, Stripe, Clio) to provide the XTND Service. Full list available upon request: <a href="mailto:support@xtnd.legal">support@xtnd.legal</a>.
          </p>

          {/* Contact Section */}
          <div className="mt-12 p-6 bg-xtnd-blue-50 dark:bg-xtnd-dark-800 rounded-lg">
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white mb-2">Questions About This Policy?</h3>
            <p className="text-xtnd-dark-600 dark:text-xtnd-dark-300">
              If you have any questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:support@xtnd.legal" className="text-xtnd-blue hover:text-xtnd-blue-600">
                support@xtnd.legal
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
